import { CommonModule } from '@angular/common';
import { Component, effect, inject, OnInit, Signal } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatOptionModule } from '@angular/material/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogActions, MatDialogRef } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Pop } from 'src/app/classes/shared/pop';
import { Project } from 'src/app/interfaces/api/project';
import { Tracker } from 'src/app/interfaces/api/tracker';
import { ModelService } from 'src/app/services/api/models/model.service';
import { ProjectService } from 'src/app/services/api/projects/project.service';
import { TrackerService } from 'src/app/services/api/trackers/tracker.service';
import { TrackService } from 'src/app/services/api/tracks/track.service';
import { UnitService } from 'src/app/services/api/units/unit.service';
import { ButtonComponent } from 'ui-library';

@Component({
  selector: 'app-resources-modal',
  templateUrl: './resources-modal.component.html',
  imports: [
    MatTableModule,
    FormsModule,
    MatTabsModule,
    MatIconModule,
    MatButtonModule,
    MatInputModule,
    CommonModule,
    TranslateModule,
    MatIconModule,
    MatPaginatorModule,
    MatOptionModule,
    MatDividerModule,
    MatDialogActions,
    MatChipsModule,
    MatToolbarModule,
    MatSelectModule,
    ButtonComponent
  ],
  standalone: true,
  styleUrls: ['./resources-modal.component.scss']
})

export class ResourcesModalComponent implements OnInit {
  // Services
  private readonly pop: Pop = inject(Pop);
  private modelService: ModelService = inject(ModelService);
  private projectService: ProjectService = inject(ProjectService);
  private trackerService: TrackerService = inject(TrackerService);
  private unitService: UnitService = inject(UnitService);
  private trackService: TrackService = inject(TrackService);
  private translate: TranslateService = inject(TranslateService);
  private dialog: MatDialog = inject(MatDialog);
  private dialogRef: MatDialogRef<ResourcesModalComponent> = inject(MatDialogRef<ResourcesModalComponent>);
  public readonly data: any = inject(MAT_DIALOG_DATA);

  // Variables
  unitsForm: any;
  currentUnit: any = {
    name: '',
    color: '#3c8dbc',
    type: 'worker',
    projects: '',
    picture: 'worker.jpg',
    editedBy: '',
    groups: [],
    trackers: [],
    collider: {
      geometry: 0,
    },
    lastPosition: {
      x: 0, y: 0, z: 0,
      lat: 0,
      lon: 0
    }
  };
  colors: any[] = [];
  availableTrackers: any[] = [];
  trackers: any[] = [];
  usedTrackers: any[] = [];
  usedTrackerIds: any[] = [];
  unitTypeArray: any[] = [];
  groups: any[] = [];
  errorName = false;
  pictures: any[] = [];
  resourceNames: any[] = [];
  unitIcons: any[] = [];
  pictureUrl: any;
  trackerNames: any;
  model: any;
  project: any;
  destroy$ = new Subject<void>();

  // Signals
  readonly projectSignal: Signal<Project> = this.projectService.currentProjectSignal;

  constructor() {
    effect(() => {
      const project = this.projectSignal();

      this.modelService.getCurrentModel(project.id).pipe(takeUntil(this.destroy$)).subscribe((res: any) => {
        this.model = res;
      })
    });
  }

  ngOnInit(): void {
    this.trackers = this.data.trackers
    this.usedTrackerIds = this.data.usedTrackerIds;
    this.resourceNames = this.data.resourceNames;
    this.colors = this.data.colors;
    this.unitTypeArray = this.data.unitTypeArray;
    this.groups = this.data.groups;
    this.unitIcons = this.data.unitIcons;
    this.pictureUrl = 'https://internaldiag236.blob.core.windows.net/unit-picture/worker.jpg';
    this.currentUnit.projects = [this.projectSignal().id];
    this.currentUnit.editedBy = this.data.user.id;
    this.selectAvalaibleTrackers();
  }

  sortResourceName(e: { target: { value: string; }; }) {
    this.errorName = false;
    this.data.resourceNames.forEach((a: string) => a.trim().toLowerCase());

    if (this.data.resourceNames.includes(e.target.value.trim().toLowerCase())) {
      this.errorName = true;
      return;
    }
  }

  selectAvalaibleTrackers() {
    this.availableTrackers = [];
    this.usedTrackers = [];

    this.trackers.forEach(t => {
      if (this.usedTrackerIds.indexOf(t.id) == -1) {

        this.availableTrackers.push(t)
      }
      else this.usedTrackers.push(t)
    });
  }

  saveUnit(rs: NgForm) {
    if (this.errorName) return;
    let unit = { ...this.currentUnit }

    if (unit.name && this.resourceNames.includes(unit.name)) {
      this.pop.toast(this.translate.instant("viewer.duplicateName"), "danger", 4000, "content_copy");
      return;
    }

    //remove null choice
    unit.trackers = this.currentUnit.trackers.filter((t: any) => t);
    if (unit.type == "worker") {
      unit.color = "#3c8dbc";
      unit.picture = "worker.jpg";
    } else if (unit.type == "equipment") {
      unit.color = "#f39c12";
      unit.picture = "wheel.jpg";
    } else if (unit.type == "virtual") {
      unit.color = "#3c8dbc";
      unit.picture = "antenna.jpg";
    } else {
      unit.color = "#dd4b39";
      unit.picture = "formwork.jpg";
    }

    if (unit.type == "virtual") {
      if (unit.trackers.length == 0) {

        this.trackerService.addTracker({ name: unit.name, project: this.project.id }).pipe(takeUntil(this.destroy$)).subscribe({
          next: (res: Tracker) => {
            unit.trackers.push(res.id);

            if (this.currentUnit.lastPosition.lat == 0 || this.currentUnit.lastPosition.lon == 0) {
              unit.lastPosition = {
                lat: this.model.origin.position.lat,
                lon: this.model.origin.position.lon,
              };
            }

            this.unitService.addUnit(unit).pipe(takeUntil(this.destroy$)).subscribe({
              next: () => {
                this.dialog.closeAll();
                this.pop.toast(this.translate.instant("trackers.add_success"), "success", 4000, "add");
              }, error: (err: any) => console.error(err)
            });

          }, error: (err: any) => console.error(err)
        });

      } else {
        this.trackerService.getTracker(unit.trackers[0]).pipe(takeUntil(this.destroy$)).subscribe((res: any) => {
          if (res.lastTrack) {
            this.trackService.getTrack(res.lastTrack).pipe(takeUntil(this.destroy$)).subscribe((res: any) => {
              if (res.lat && res.lon) {
                unit.lastPosition = {
                  lat: res.lat,
                  lon: res.lon,
                  z: res.z,
                };
              } else {
                unit.lastPosition = {
                  x: res.x,
                  y: res.y,
                  z: res.z,
                };
              }

            }, err => console.error(err))
          }
        }, err => console.error(err))

        this.unitService.addUnit(unit).pipe(takeUntil(this.destroy$)).subscribe({
          next: () => {
            this.dialog.closeAll();
          }, error: (err: any) => console.error(err)
        });
      }
    } else {
      this.unitService.addUnit(unit).pipe(takeUntil(this.destroy$)).subscribe({
        next: () => {
          this.dialog.closeAll();
        }, error: (err: any) => console.error(err)
      });
    }
  }

  unitSelectBtn(evt: { source: { ngControl: { name: string; }; }; value: any; }) {
    this.currentUnit[evt.source.ngControl.name] = evt.value;
    if (evt.source.ngControl.name == 'picture') this.pictureUrl = `https://internaldiag236.blob.core.windows.net/unit-picture/${evt.value}`;
  }

  closeDialog(result: any = null): void {
    this.dialogRef.close(result);
  }
}
